import React, { useState } from 'react'
import EditIcon from '@material-ui/icons/Edit'

import { ERRORS } from 'consts'
import { useApi } from 'hooks'
import { snackbar } from 'components'
import { PageHeader } from 'components/PageHeader'

import { getTags } from './api'
import { DefaultTable } from '../components'
import { GetTagsResponse, Tag } from './types'
import {
  CreateTagDialog,
  DeleteTagDialog,
  UpdateTagDialog,
  CoolingDownConfigDialog,
} from './components'
import { TAG_GROUPS, USAGE_TAG_GROUPS } from './consts'

const TABS = [
  { key: 'usage', label: 'Tags por uso' },
  { key: 'stores', label: 'Tags de Locales' },
  { key: 'test', label: 'Tags de test' },
  { key: 'csv', label: 'Tags de carga masiva' },
]

const removeAccents = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const columns = [
  { title: 'ID', field: 'id' },
  {
    title: 'Título',
    field: 'title',
    customFilterAndSearch: (term: string, rowData: Tag) =>
      rowData.title !== null &&
      removeAccents(rowData.title)
        .toLowerCase()
        .indexOf(removeAccents(term).toLowerCase()) !== -1,
  },
  {
    title: 'Descripción',
    field: 'description',
    customFilterAndSearch: (term: string, rowData: Tag) =>
      rowData.description !== null &&
      removeAccents(rowData.description)
        .toLowerCase()
        .indexOf(removeAccents(term).toLowerCase()) !== -1,
  },
  {
    title: 'Activo',
    render: (rowData: Tag) => (rowData.active ? 'Sí' : 'No'),
    sorting: false,
  },
]

const getTagsResponseGetter = (responseData: GetTagsResponse) => responseData?.tags ?? []

const Tags: React.FC = () => {
  const [{ data: tags, isLoading }, , setTags] = useApi<GetTagsResponse, Tag[]>(
    getTags,
    getTagsResponseGetter,
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )
  const [isCreateTagDialogOpen, setIsCreateTagDialogOpen] = useState(false)
  const [isDeleteTagDialogOpen, setIsDeleteTagDialogOpen] = useState(false)
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null)
  const [isUpdateTagDialogOpen, setIsUpdateTagDialogOpen] = useState(false)
  const [tagToUpdate, setTagToUpdate] = useState<Tag | null>(null)
  const [isCoolingDownDialogOpen, setIsCoolingDownDialogOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('usage')

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey)
  }

  const usageTags = tags.filter(
    tag => tag.group && (USAGE_TAG_GROUPS as readonly string[]).includes(tag.group),
  )
  const storesTags = tags.filter(tag => tag.group === TAG_GROUPS.STORES)
  const testTags = tags.filter(tag => !tag.group || tag.group === '')
  const csvTags = tags.filter(tag => tag.group === TAG_GROUPS.FROM_CSV)

  const getCurrentTags = () => {
    switch (activeTab) {
      case 'usage':
        return usageTags
      case 'stores':
        return storesTags
      case 'test':
        return testTags
      case 'csv':
        return csvTags
      default:
        return []
    }
  }

  const currentTags = getCurrentTags()

  const isCoolingDownTag = (tag: Tag) =>
    tag.group === TAG_GROUPS.COOLING_DOWN || tag.code === 'COOLING_DOWN'

  const isEditable = (tag: Tag) => {
    if (activeTab === 'usage') {
      return isCoolingDownTag(tag)
    }
    if (activeTab !== 'test' && activeTab !== 'csv') {
      return false
    }
    // Tags de test (sin grupo) y Tags de carga masiva son editables
    const isTestTag = !tag.group || tag.group === ''
    const isCsvTag = tag.group === TAG_GROUPS.FROM_CSV
    return isTestTag || isCsvTag
  }

  const handleCreateTagDone = (newTag: Tag) => {
    setTags([...tags, newTag])
    setIsCreateTagDialogOpen(false)
  }

  const handleDeleteTagDialogClose = () => {
    setIsDeleteTagDialogOpen(false)
    setTagToDelete(null)
  }

  const handleDeleteTagDone = () => {
    if (!tagToDelete) return
    setTags(tags.filter(tag => tag.id !== tagToDelete.id))
    handleDeleteTagDialogClose()
  }

  const handleUpdateTagDialogClose = () => {
    setIsUpdateTagDialogOpen(false)
    setTagToUpdate(null)
  }

  const handleUpdateTagDone = (updatedTag: Tag) => {
    setTags(
      tags.map(tag => {
        if (tag.id === updatedTag.id) {
          return updatedTag
        }
        return tag
      }),
    )
    handleUpdateTagDialogClose()
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Nueva etiqueta(tag)',
      isFreeAction: true,
      onClick: () => setIsCreateTagDialogOpen(true),
      hidden: activeTab !== 'test' && activeTab !== 'csv', // Solo mostrar en Tags de test y Tags de carga masiva
    },
    (row: Tag) => ({
      icon: () => <EditIcon />,
      tooltip: isCoolingDownTag(row) ? 'Configurar días de inactividad' : 'Editar etiqueta(tag)',
      onClick: (_: React.SyntheticEvent, rowData: Tag | Tag[]) => {
        const tag = rowData as Tag
        if (isCoolingDownTag(tag)) {
          setIsCoolingDownDialogOpen(true)
          return
        }
        if (isEditable(tag)) {
          setTagToUpdate(tag)
          setIsUpdateTagDialogOpen(true)
        }
      },
      hidden: !isEditable(row),
    }),
    /* (row: Tag) => ({
      icon: () => <DeleteIcon />,
      tooltip: 'Eliminar etiqueta(tag)',
      onClick: (_: React.SyntheticEvent, rowData: Tag | Tag[]) => {
        const tag = rowData as Tag
        if (isEditable(tag)) {
          setTagToDelete(tag)
          setIsDeleteTagDialogOpen(true)
        }
      },
      hidden: !isEditable(row), // Ocultar para tags no editables
    }), */
  ]

  const hideActivoColumn = activeTab === 'usage' || activeTab === 'stores'
  const hideActionsColumn = activeTab === 'stores'
  const tableColumns = hideActivoColumn ? columns.filter(col => col.title !== 'Activo') : columns
  const tableActions = hideActionsColumn ? [] : actions

  return (
    <>
      <PageHeader title="Tags" tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} />
      <DefaultTable
        title=""
        columns={tableColumns}
        data={currentTags}
        isLoading={isLoading}
        actions={tableActions}
        options={{
          showFirstLastPageButtons: true,
          toolbar: true,
          actionsColumnIndex: -1,
        }}
      />
      {isCreateTagDialogOpen && (
        <CreateTagDialog
          handleClose={() => setIsCreateTagDialogOpen(false)}
          onDone={handleCreateTagDone}
          tagGroup={activeTab === 'csv' ? TAG_GROUPS.FROM_CSV : undefined}
        />
      )}
      {isDeleteTagDialogOpen && tagToDelete && (
        <DeleteTagDialog
          handleClose={handleDeleteTagDialogClose}
          tagId={tagToDelete.id}
          tagTitle={tagToDelete.title}
          onDone={handleDeleteTagDone}
        />
      )}
      {isUpdateTagDialogOpen && tagToUpdate && (
        <UpdateTagDialog
          handleClose={handleUpdateTagDialogClose}
          tagToUpdate={tagToUpdate}
          onDone={handleUpdateTagDone}
        />
      )}
      {isCoolingDownDialogOpen && (
        <CoolingDownConfigDialog
          handleClose={() => setIsCoolingDownDialogOpen(false)}
          onDone={() => setIsCoolingDownDialogOpen(false)}
        />
      )}
    </>
  )
}

export { Tags }
